if (!customElements.get("cart-free-gift-picker")) {
  class CartFreeGiftPicker extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.swiperEl = this.querySelector("[data-free-gift-swiper]");
      this.threshold = parseFloat(this.dataset.threshold || 125) * 100; // Convert to cents
      this.cartUpdateUnsubscriber = null;
    }

    connectedCallback() {
      this.initSwiper();
      this.setupEventListeners();
      // Small delay to ensure cart state is ready, especially after DOM updates
      setTimeout(() => {
        this.updatePickerVisibility();
      }, 50);
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    initSwiper() {
      if (!this.swiperEl || typeof Swiper === "undefined") {
        return;
      }

      const paginationEl = this.querySelector(".free-gift__carousel-dots");

      // One card per view
      this.swiper = new Swiper(this.swiperEl, {
        slidesPerView: 1,
        spaceBetween: 16,
        pagination: {
          el: paginationEl,
          clickable: true,
          bulletClass: "swiper-pagination-bullet",
          bulletActiveClass: "swiper-pagination-bullet-active",
        },
      });

      // Setup color select and claim button handlers
      this.setupProductHandlers();
    }

    setupProductHandlers() {
      const selects = this.querySelectorAll(".free-gift-item__select select");
      const buttons = this.querySelectorAll(".free-gift-item__add");

      // Store default button labels once so we can restore them later
      buttons.forEach((button) => {
        if (!button.dataset.defaultLabel) {
          const labelSpan = button.querySelector("span");
          if (labelSpan) {
            button.dataset.defaultLabel = labelSpan.textContent.trim();
          }
        }
      });

      selects.forEach((select) => {
        const button = select
          .closest(".free-gift-item")
          .querySelector(".free-gift-item__add");

        // Check if select already has a valid value selected
        const currentValue = select.value;
        if (currentValue && currentValue !== "Select Color") {
          button.disabled = false;
          button.dataset.variantId = currentValue;
        } else {
          // Reset to default state
          select.value = "Select Color";
          button.disabled = true;
          delete button.dataset.variantId;
        }

        // Only add listener if not already added
        if (!select.dataset.handlerAttached) {
          select.dataset.handlerAttached = "true";
          select.addEventListener("change", (e) => {
            const variantId = e.target.value;

            if (variantId && variantId !== "Select Color") {
              button.disabled = false;
              button.dataset.variantId = variantId;
            } else {
              button.disabled = true;
              delete button.dataset.variantId;
            }
          });
        }
      });

      buttons.forEach((button) => {
        // Only add listener if not already added
        if (!button.dataset.handlerAttached) {
          button.dataset.handlerAttached = "true";
          button.addEventListener("click", (e) => {
            e.preventDefault();
            this.claimGift(button);
          });
        }
      });
    }

    resetButtonsToDefaultState() {
      const buttons = this.querySelectorAll(".free-gift-item__add");

      buttons.forEach((button) => {
        // Remove loading state
        button.classList.remove("btn--loading");

        // Restore default label text if it was captured
        const labelSpan = button.querySelector("span");
        const defaultLabel = button.dataset.defaultLabel;

        if (labelSpan && defaultLabel) {
          labelSpan.textContent = defaultLabel;
        }

        // For single-variant gifts (no select), re-enable the button
        // whenever a valid variant id is present.
        if (button.dataset.variantId) {
          button.disabled = false;
        }
      });
    }

    syncButtonStates() {
      // Sync button enabled/disabled state with select values
      const selects = this.querySelectorAll(".free-gift-item__select select");

      selects.forEach((select) => {
        const button = select
          .closest(".free-gift-item")
          .querySelector(".free-gift-item__add");
        const variantId = select.value;

        if (variantId && variantId !== "Select Color") {
          button.disabled = false;
          button.dataset.variantId = variantId;
        } else {
          button.disabled = true;
          delete button.dataset.variantId;
        }
      });
    }

    async claimGift(button) {
      const variantId = button.dataset.variantId;
      const productId = button.dataset.productId;
      const productHandle = button.dataset.productHandle;

      if (!variantId) {
        console.error("No variant selected");
        return;
      }

      button.disabled = true;
      button.classList.add("btn--loading");

      try {
        const PUB_SUB_EVENTS = window.PUB_SUB_EVENTS || {
          cartUpdate: "cart-update",
        };

        const response = await fetch(
          `${window.FoxThemeSettings.routes.cart_add_url}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: 1,
                  properties: {
                    _free_gift: "true",
                  },
                },
              ],
              sections: ["cart-drawer"],
              sections_url: window.location.pathname,
            }),
          }
        );

        const data = await response.json();

        if (data.status) {
          console.error("Error adding gift:", data.description);
          button.disabled = false;
          button.classList.remove("btn--loading");
          return;
        }

        // Hide picker immediately to prevent double-clicks
        this.style.display = "none";

        // Update cart drawer if it exists
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && cartDrawer.renderContents) {
          await cartDrawer.renderContents(data);
        }

        // Emit cart update event AFTER cart drawer is updated
        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, data);
      } catch (error) {
        console.error("Error claiming gift:", error);
        button.disabled = false;
        button.classList.remove("btn--loading");
      }
    }

    setupEventListeners() {
      const PUB_SUB_EVENTS = window.PUB_SUB_EVENTS || {
        cartUpdate: "cart-update",
      };

      this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (state) => {
          this.updatePickerVisibility(state);
        }
      );
    }

    async updatePickerVisibility(cartState = null) {
      try {
        let cart = cartState;

        const hasCompleteData =
          cart &&
          (cart.items_subtotal_price !== undefined ||
            cart.total_price !== undefined);

        if (!hasCompleteData) {
          cart = await window.FoxThemeCartHelpers.getState();
        }

        if (!cart) {
          return;
        }

        // Calculate subtotal excluding free gifts and check for free gift in one pass
        let subtotal = cart.items_subtotal_price || cart.total_price || 0;
        let hasFreeGift = false;

        if (cart.items) {
          cart.items.forEach((item) => {
            if (item.properties && item.properties._free_gift === "true") {
              hasFreeGift = true;
              const giftPrice = item.final_line_price || 0;
              subtotal -= giftPrice;
            }
          });
        }

        // Check if threshold is met
        const thresholdMet = subtotal >= this.threshold;

        // Auto-remove free gift if threshold is no longer met
        if (hasFreeGift && !thresholdMet) {
          // Find the free gift item and calculate its line number
          const freeGiftItem = cart.items.find(
            (item) => item.properties && item.properties._free_gift === "true"
          );

          if (freeGiftItem) {
            // Calculate the line number (1-indexed for Shopify Cart API)
            const lineNumber = cart.items.indexOf(freeGiftItem) + 1;
            this.removeFreeGift(lineNumber);
            return; // Exit early, will update again after removal
          }
        }

        // Show/hide picker based on conditions
        if (thresholdMet && !hasFreeGift) {
          this.style.display = "";
          // Ensure buttons are reset to their default visual state
          this.resetButtonsToDefaultState();
          // Sync button states with select values
          this.syncButtonStates();
        } else {
          this.style.display = "none";
        }
      } catch (error) {
      }
    }

    async removeFreeGift(lineNumber) {
      try {
        const response = await fetch(
          `${window.FoxThemeSettings.routes.cart_change_url}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              line: lineNumber,
              quantity: 0,
              sections: ["cart-drawer"],
              sections_url: window.location.pathname,
            }),
          }
        );

        const data = await response.json();

        if (data.status) {
          return;
        }

        // Emit cart update event
        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, data);

        // Update cart drawer if it exists
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && cartDrawer.renderContents) {
          cartDrawer.renderContents(data);
        }
      } catch (error) {
      }
    }
  }

  customElements.define("cart-free-gift-picker", CartFreeGiftPicker);
}
